import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { trackEvent } from "@/lib/track";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ClipboardCheck,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Images,
  Upload,
  Loader2,
  CheckCircle2,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Sprint 3 + Sprint 4 Vision AI — the unified review drawer bound to a
 * single generated image. Has two tabs:
 *   - "Evaluare" (C.1 + C.2 + C.3): positives, negatives, free notes
 *   - "Comparație" (C.4): upload a reference "OK" render + explanation
 * Access is partner/sales/admin (parent gates the trigger).
 */

const MIN_SIG_READY = 3;   // spec: ≥3 positives AND ≥3 negatives for signature-eligible

export default function VisionAiReviewDrawer({ sim, image, open, onOpenChange }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("review");
  const [codes, setCodes] = useState({ positives: [], negatives: [] });
  const [positives, setPositives] = useState([]);    // [{code, reason}]
  const [negatives, setNegatives] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [existingComparison, setExistingComparison] = useState(null);

  // Comparison state
  const [refFile, setRefFile] = useState(null);
  const [refPreview, setRefPreview] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [markedEquivalent, setMarkedEquivalent] = useState(false);
  const [uploadingComp, setUploadingComp] = useState(false);

  // Load taxonomy + existing feedback + existing comparison when opening
  useEffect(() => {
    if (!open) return;
    api.get("/vision/feedback/codes").then((r) => setCodes(r.data || { positives: [], negatives: [] })).catch(() => {});
    api
      .get(`/simulations/${sim.id}/images/${image.id}/vision-feedback`)
      .then((r) => {
        const mine = r.data?.mine;
        if (mine) {
          setExistingReview(mine);
          setPositives(mine.positives || []);
          setNegatives(mine.negatives || []);
          setNotes(mine.notes || "");
        } else {
          setExistingReview(null);
          setPositives([]);
          setNegatives([]);
          setNotes("");
        }
      })
      .catch(() => {});
    api
      .get(`/simulations/${sim.id}/images/${image.id}/vision-comparison`)
      .then((r) => {
        const mine = r.data?.mine;
        if (mine) {
          setExistingComparison(mine);
          setExplanation(mine.explanation || "");
          setMarkedEquivalent(!!mine.marked_equivalent);
        } else {
          setExistingComparison(null);
          setExplanation("");
          setMarkedEquivalent(false);
        }
      })
      .catch(() => {});
    setRefFile(null);
    setRefPreview(null);
  }, [open, sim.id, image.id]);

  const togglePositive = (code) => {
    setPositives((prev) => {
      const idx = prev.findIndex((p) => p.code === code);
      if (idx >= 0) return prev.filter((p) => p.code !== code);
      return [...prev, { code, reason: "" }];
    });
  };
  const setPositiveReason = (code, reason) => {
    setPositives((prev) => prev.map((p) => (p.code === code ? { ...p, reason } : p)));
  };

  const toggleNegative = (code) => {
    setNegatives((prev) => {
      const idx = prev.findIndex((p) => p.code === code);
      if (idx >= 0) return prev.filter((p) => p.code !== code);
      return [...prev, { code, reason: "" }];
    });
  };
  const setNegativeReason = (code, reason) => {
    setNegatives((prev) => prev.map((p) => (p.code === code ? { ...p, reason } : p)));
  };

  const isSignatureReady = useMemo(
    () => positives.length >= MIN_SIG_READY && negatives.length >= MIN_SIG_READY,
    [positives.length, negatives.length],
  );

  const submitReview = async () => {
    if (!positives.length && !negatives.length && !notes.trim()) {
      toast.error(t("vision_ai.feedback_err_empty"));
      return;
    }
    setSaving(true);
    try {
      const body = {
        positives: positives.map((p) => ({ code: p.code, reason: (p.reason || "").trim() || null })),
        negatives: negatives.map((p) => ({ code: p.code, reason: (p.reason || "").trim() || null })),
        notes: notes.trim() || null,
      };
      const r = await api.post(
        `/simulations/${sim.id}/images/${image.id}/vision-feedback`,
        body,
      );
      setExistingReview(r.data);
      trackEvent("vision.feedback.submit", {
        sim_id: sim.id,
        image_id: image.id,
        positives: positives.length,
        negatives: negatives.length,
        signature_ready: !!r.data?.signature_ready,
      });
      toast.success(
        r.data.signature_ready
          ? t("vision_ai.feedback_saved_sigready")
          : t("vision_ai.feedback_saved_plain"),
      );
    } catch (e) {
      toast.error(t("vision_ai.feedback_err_save"));
    } finally {
      setSaving(false);
    }
  };

  const onRefChange = (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error(t("vision_ai.compare_err_size"));
      return;
    }
    setRefFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setRefPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const submitComparison = async () => {
    if (!existingComparison && !refFile) {
      toast.error(t("vision_ai.compare_err_no_file"));
      return;
    }
    if (!explanation.trim()) {
      toast.error(t("vision_ai.compare_err_no_explain"));
      return;
    }
    setUploadingComp(true);
    try {
      const fd = new FormData();
      // If editing an existing comparison but not replacing the file,
      // require a fresh file re-upload (backend endpoint is a full POST).
      // To keep UX simple, we always require the file on submit.
      if (!refFile) {
        toast.error(t("vision_ai.compare_err_need_reupload"));
        setUploadingComp(false);
        return;
      }
      fd.append("reference_file", refFile);
      fd.append("explanation", explanation.trim());
      fd.append("marked_equivalent", markedEquivalent ? "true" : "false");
      const r = await api.post(
        `/simulations/${sim.id}/images/${image.id}/vision-comparison`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setExistingComparison(r.data);
      setRefFile(null);
      setRefPreview(null);
      toast.success(t("vision_ai.compare_saved"));
    } catch (e) {
      toast.error(t("vision_ai.compare_err_save"));
    } finally {
      setUploadingComp(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl bg-zinc-950 border-white/10 text-white p-0 overflow-hidden"
        data-testid="vision-review-drawer"
      >
        <DialogHeader className="p-5 sm:p-6 border-b border-white/5 bg-gradient-to-br from-zinc-950 to-zinc-900">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-2 flex items-center gap-2">
            <Sparkles size={12} strokeWidth={1.5} />
            {t("vision_ai.review_eyebrow")}
          </div>
          <DialogTitle className="font-outfit font-black text-2xl tracking-tight text-white">
            {t("vision_ai.review_title")}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {t("vision_ai.review_subtitle")}
          </DialogDescription>
          {/* Simple tab switcher */}
          <div className="flex items-center gap-1 mt-4 border-b border-white/5 -mb-5 sm:-mb-6">
            <button
              type="button"
              onClick={() => setTab("review")}
              data-testid="vision-review-tab-review"
              data-active={tab === "review" ? "true" : "false"}
              className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px ${
                tab === "review"
                  ? "border-orange-500 text-white"
                  : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <ClipboardCheck size={14} />
                {t("vision_ai.review_tab_review")}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setTab("compare")}
              data-testid="vision-review-tab-compare"
              data-active={tab === "compare" ? "true" : "false"}
              className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px ${
                tab === "compare"
                  ? "border-orange-500 text-white"
                  : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <Images size={14} />
                {t("vision_ai.review_tab_compare")}
              </span>
            </button>
          </div>
        </DialogHeader>

        <div className="p-5 sm:p-6 max-h-[65vh] overflow-y-auto">
          {tab === "review" ? (
            <div className="space-y-6">
              {/* Signature-ready indicator */}
              <div
                className={`text-xs px-3 py-2 rounded-lg border flex items-center gap-2 ${
                  isSignatureReady
                    ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300"
                    : "border-white/10 bg-zinc-900/40 text-zinc-400"
                }`}
                data-testid="vision-review-sig-status"
                data-signature-ready={isSignatureReady ? "true" : "false"}
              >
                <CheckCircle2 size={12} className={isSignatureReady ? "text-emerald-400" : "text-zinc-500"} />
                <span>
                  {isSignatureReady
                    ? t("vision_ai.feedback_sig_ready")
                    : t("vision_ai.feedback_need_more", {
                        p: Math.max(0, MIN_SIG_READY - positives.length),
                        n: Math.max(0, MIN_SIG_READY - negatives.length),
                      })}
                </span>
              </div>

              {/* Positives (C.1) */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <ThumbsUp size={14} strokeWidth={1.5} className="text-emerald-400" />
                  <h3 className="text-sm font-outfit font-bold uppercase tracking-[0.2em] text-white">
                    {t("vision_ai.feedback_c1_title")}
                  </h3>
                  <span className="ml-auto text-[10px] text-zinc-500">
                    {positives.length}/{MIN_SIG_READY}+
                  </span>
                </div>
                <div className="space-y-2">
                  {codes.positives?.map((c) => {
                    const item = positives.find((p) => p.code === c.code);
                    const selected = !!item;
                    return (
                      <div
                        key={c.code}
                        className={`rounded-lg border p-2.5 transition ${
                          selected
                            ? "border-emerald-500/30 bg-emerald-500/5"
                            : "border-white/10 bg-zinc-950/50 hover:border-emerald-500/20"
                        }`}
                      >
                        <label className="flex items-start gap-2 cursor-pointer" data-testid={`vision-pos-${c.code}`}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => togglePositive(c.code)}
                            className="mt-1 accent-emerald-500"
                            data-testid={`vision-pos-check-${c.code}`}
                          />
                          <span className="text-sm text-zinc-200 flex-1">{c.label}</span>
                        </label>
                        {selected && (
                          <input
                            type="text"
                            value={item.reason || ""}
                            onChange={(e) => setPositiveReason(c.code, e.target.value.slice(0, 400))}
                            placeholder={t("vision_ai.feedback_why_placeholder")}
                            className="w-full mt-2 rounded border border-white/10 bg-zinc-900 px-2 py-1.5 text-xs text-white placeholder:text-zinc-500 outline-none focus:border-emerald-500/40"
                            data-testid={`vision-pos-reason-${c.code}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Negatives (C.2) */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <ThumbsDown size={14} strokeWidth={1.5} className="text-rose-400" />
                  <h3 className="text-sm font-outfit font-bold uppercase tracking-[0.2em] text-white">
                    {t("vision_ai.feedback_c2_title")}
                  </h3>
                  <span className="ml-auto text-[10px] text-zinc-500">
                    {negatives.length}/{MIN_SIG_READY}+
                  </span>
                </div>
                <div className="space-y-2">
                  {codes.negatives?.map((c) => {
                    const item = negatives.find((p) => p.code === c.code);
                    const selected = !!item;
                    return (
                      <div
                        key={c.code}
                        className={`rounded-lg border p-2.5 transition ${
                          selected
                            ? "border-rose-500/30 bg-rose-500/5"
                            : "border-white/10 bg-zinc-950/50 hover:border-rose-500/20"
                        }`}
                      >
                        <label className="flex items-start gap-2 cursor-pointer" data-testid={`vision-neg-${c.code}`}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleNegative(c.code)}
                            className="mt-1 accent-rose-500"
                            data-testid={`vision-neg-check-${c.code}`}
                          />
                          <span className="text-sm text-zinc-200 flex-1">{c.label}</span>
                        </label>
                        {selected && (
                          <input
                            type="text"
                            value={item.reason || ""}
                            onChange={(e) => setNegativeReason(c.code, e.target.value.slice(0, 400))}
                            placeholder={t("vision_ai.feedback_why_placeholder")}
                            className="w-full mt-2 rounded border border-white/10 bg-zinc-900 px-2 py-1.5 text-xs text-white placeholder:text-zinc-500 outline-none focus:border-rose-500/40"
                            data-testid={`vision-neg-reason-${c.code}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Notes (C.3) */}
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare size={14} strokeWidth={1.5} className="text-zinc-400" />
                  <h3 className="text-sm font-outfit font-bold uppercase tracking-[0.2em] text-white">
                    {t("vision_ai.feedback_c3_title")}
                  </h3>
                </div>
                <p className="text-xs text-zinc-500 mb-2">{t("vision_ai.feedback_c3_body")}</p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, 1500))}
                  placeholder={t("vision_ai.feedback_c3_placeholder")}
                  rows={4}
                  className="bg-zinc-950/60 border-white/10 focus:border-orange-500/40"
                  data-testid="vision-feedback-notes"
                />
                <div className="text-[10px] text-zinc-500 text-right mt-1">
                  {notes.length}/1500
                </div>
              </section>
            </div>
          ) : (
            <div className="space-y-5">
              <p className="text-xs text-zinc-400 max-w-2xl">
                {t("vision_ai.compare_body")}
              </p>
              {/* Side-by-side preview */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    {t("vision_ai.compare_reference_label")}
                  </div>
                  <label
                    className="block border-2 border-dashed border-white/10 hover:border-emerald-500/40 rounded-lg overflow-hidden aspect-[4/3] bg-zinc-950/50 cursor-pointer transition"
                    data-testid="vision-compare-ref-drop"
                  >
                    {refPreview || existingComparison?.reference_image_url ? (
                      <img
                        src={refPreview || fileUrl(existingComparison.reference_image_url)}
                        alt="reference"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500 gap-2">
                        <Upload size={22} strokeWidth={1.5} />
                        <span className="text-xs">{t("vision_ai.compare_upload_ref")}</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onRefChange(e.target.files?.[0])}
                      data-testid="vision-compare-ref-input"
                    />
                  </label>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                    {t("vision_ai.compare_onefdp_label")}
                  </div>
                  <div className="rounded-lg overflow-hidden aspect-[4/3] bg-zinc-950/50 border border-white/10">
                    <img
                      src={fileUrl(image.url)}
                      alt="onefdp"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markedEquivalent}
                  onChange={(e) => setMarkedEquivalent(e.target.checked)}
                  className="accent-orange-500"
                  data-testid="vision-compare-equiv"
                />
                {t("vision_ai.compare_mark_equivalent")}
              </label>

              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-300 font-semibold mb-2 block">
                  {t("vision_ai.compare_explanation_label")}
                </label>
                <Textarea
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value.slice(0, 1500))}
                  placeholder={t("vision_ai.compare_explanation_placeholder")}
                  rows={5}
                  className="bg-zinc-950/60 border-white/10 focus:border-orange-500/40"
                  data-testid="vision-compare-explanation"
                />
                <div className="text-[10px] text-zinc-500 text-right mt-1">
                  {explanation.length}/1500
                </div>
              </div>

              {existingComparison && (
                <div className="text-xs text-zinc-500 border border-white/5 rounded p-3">
                  {t("vision_ai.compare_prior_notice")}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-white/5 flex items-center justify-between gap-3 bg-zinc-950">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-sm text-zinc-500 hover:text-white transition inline-flex items-center gap-1"
            data-testid="vision-review-close"
          >
            <X size={14} />
            {t("common.close")}
          </button>
          {tab === "review" ? (
            <Button
              type="button"
              onClick={submitReview}
              disabled={saving}
              data-testid="vision-review-save-review"
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {saving ? (
                <><Loader2 size={16} className="animate-spin" /> {t("common.saving")}</>
              ) : (
                <><CheckCircle2 size={16} /> {t("vision_ai.feedback_save_btn")}</>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={submitComparison}
              disabled={uploadingComp}
              data-testid="vision-review-save-compare"
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {uploadingComp ? (
                <><Loader2 size={16} className="animate-spin" /> {t("common.saving")}</>
              ) : (
                <><Upload size={16} /> {t("vision_ai.compare_save_btn")}</>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
